import { useState } from "react";
import {
  Button as MuiButton,
  CircularProgress as MuiCircularProgress,
  FormControlLabel as MuiFormControlLabel,
  IconButton as MuiIconButton,
  InputAdornment as MuiInputAdornment,
  Stack as MuiStack,
  Typography as MuiTypography,
  Tooltip as MuiTooltip,
} from "@mui/material";
import { Info as MuiInfoIcon } from "@mui/icons-material";
import { Field, useFormikContext } from "formik";
import { Trans, useTranslation } from "react-i18next";
import { Theme } from "common";
import FormikAutocomplete from "app/shared/UI/FormikAutocomplete";
import FormikCheckbox from "app/shared/UI/FormikCheckbox";
import FormikTextField from "app/shared/UI/FormikTextField";
import ConfirmationDialog from "app/shared/UI/Dialog/ConfirmationDialog";
import InfoDialog from "app/shared/UI/Dialog/InfoDialog";
import {
  INFO_DIALOG_KEYS,
  LOADER_TYPES,
} from "../../services/StudyRecruitmentService/constants";
import { initOtherStudyContact } from "../../services/StudyRecruitmentService/initialStates";
import { formatMainContactName } from "../../services/StudyRecruitmentService/serviceHelpers";
import { useStudyRecruitment } from "app/services/StudyRecruitmentService/StudyRecruitmentProvider";

const boxStyles = {
  padding: "16px",
  backgroundColor: Theme.palette.primary.contrastText,
  borderRadius: "8px",
  boxShadow: "2px 10px 10px rgba(0, 0, 0, 0.1)",
  width: "834px",
  border: "1px solid #C4C4C4",
};

const formLabelStyles = {
  fontSize: "14px",
  fontWeight: 700,
  lineHeight: "18px",
};

const formTextFieldStyles = {
  "& .MuiInputBase-root:not(.MuiInputBase-multiline)": {
    height: "51px",
  },
  "& .MuiInputBase-input": {
    fontSize: "14px",
    lineHeight: "18px",
  },
  "& .MuiInputLabel-root": {
    fontSize: "14px",
    lineHeight: "18px",
  },
};

const formCheckboxStyles = {
  label: {
    margin: "2px 0 0",
    "& .MuiFormControlLabel-label": {
      fontSize: "10px",
      lineHeight: "13px",
    },
  },
  checkbox: { width: "20px", height: "20px", margin: "0 -12px 0 10px" },
};

const formInfoIconStyles = {
  button: { p: 0 },
  icon: { width: "18px", height: "18px" },
};

const buttonStyles = { width: "120px", height: "44px" };

const StudyRecruitmentForm = () => {
  const { t } = useTranslation();
  const [showDiscardConfirmationDialog, setShowDiscardConfirmationDialog] =
    useState(false);
  const [showSubmitConfirmationDialog, setShowSubmitConfirmationDialog] =
    useState(false);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [infoDialogKey, setInfoDialogKey] = useState(null);

  const { isLoading } = useStudyRecruitment();

  const {
    values,
    dirty,
    isSubmitting,
    isValid,
    resetForm,
    setFieldValue,
    submitForm,
  } = useFormikContext();

  const {
    STUDY_RECRUITMENT_CONTACT,
    OFFICIAL_STUDY_TITLE,
    STUDY_TITLE,
    PURPOSE,
    INCLUSION_CRITERIA,
    EXCLUSION_CRITERIA,
    PRE_SCREENING_QUESTIONNAIRE,
  } = INFO_DIALOG_KEYS;

  const infoDialogTitle = () => {
    switch (infoDialogKey) {
      case STUDY_RECRUITMENT_CONTACT:
        return t("studyRecruitment.infoDialog.studyRecruitmentContact.title");
      case OFFICIAL_STUDY_TITLE:
        return t("studyRecruitment.infoDialog.officialStudyTitle.title");
      case STUDY_TITLE:
        return t("studyRecruitment.infoDialog.studyTitle.title");
      case PURPOSE:
        return t("studyRecruitment.infoDialog.purpose.title");
      case INCLUSION_CRITERIA:
        return t("studyRecruitment.infoDialog.inclusionCriteria.title");
      case EXCLUSION_CRITERIA:
        return t("studyRecruitment.infoDialog.exclusionCriteria.title");
      case PRE_SCREENING_QUESTIONNAIRE:
        return t("studyRecruitment.infoDialog.preQuestionnaire.title");
      default:
        return "";
    }
  };

  const infoDialogContent = () => {
    switch (infoDialogKey) {
      case STUDY_RECRUITMENT_CONTACT:
        return t("studyRecruitment.infoDialog.studyRecruitmentContact.content");
      case OFFICIAL_STUDY_TITLE:
        return t("studyRecruitment.infoDialog.officialStudyTitle.content");
      case STUDY_TITLE:
        return t("studyRecruitment.infoDialog.studyTitle.content");
      case PURPOSE:
        return t("studyRecruitment.infoDialog.purpose.content");
      case INCLUSION_CRITERIA:
        return t("studyRecruitment.infoDialog.inclusionCriteria.content");
      case EXCLUSION_CRITERIA:
        return t("studyRecruitment.infoDialog.exclusionCriteria.content");
      case PRE_SCREENING_QUESTIONNAIRE:
        return t("studyRecruitment.infoDialog.preQuestionnaire.content");
      default:
        return "";
    }
  };

  const handleInfoDialogOpen = (event, key) => {
    event.currentTarget?.blur();
    setInfoDialogKey(key);
    setOpenInfoDialog(true);
  };

  const handleInfoDialogClose = () => {
    setOpenInfoDialog(false);
  };

  const handleDiscardConfirmationDialogClose = (choice) => {
    if (choice) {
      resetForm();
    }
    setShowDiscardConfirmationDialog(false);
  };

  const handleSubmitConfirmationDialogClose = (choice) => {
    if (choice) {
      submitForm();
    }
    setShowSubmitConfirmationDialog(false);
  };

  return (
    <MuiStack gap={1}>
      <MuiTypography sx={{ fontWeight: 600, color: "black", fontSize: "20px" }}>
        {t("studyRecruitment.sectionTitles.form")}
      </MuiTypography>
      <MuiStack gap={3} sx={boxStyles}>
        <MuiStack gap={2}>
          <MuiStack gap={1.5}>
            <MuiStack alignItems="center" direction="row" gap={0.5}>
              <MuiTypography sx={formLabelStyles}>
                {t("studyRecruitment.labels.studyRecruitmentContact")}
              </MuiTypography>
              <MuiTooltip
                title={t("studyRecruitment.tooltips.info")}
                placement="bottom"
              >
                <MuiIconButton
                  onClick={(event) =>
                    handleInfoDialogOpen(event, STUDY_RECRUITMENT_CONTACT)
                  }
                  sx={formInfoIconStyles.button}
                >
                  <MuiInfoIcon sx={formInfoIconStyles.icon} />
                </MuiIconButton>
              </MuiTooltip>
            </MuiStack>

            <MuiStack direction="row" gap={1.5}>
              <MuiStack gap={0.25}>
                <Field
                  component={FormikAutocomplete}
                  name="name"
                  label="studyRecruitment.form.name"
                  required
                  disableClearable
                  options={values.studyContacts}
                  onChange={(val, setName) => {
                    if (!val) {
                      setFieldValue("studyContactId", "");
                      setName(null);
                      setFieldValue("email", "");
                      setFieldValue("phone", "");
                      setFieldValue("nameVisibility", false);
                      setFieldValue("emailVisibility", false);
                      setFieldValue("phoneVisibility", false);
                      return;
                    }

                    setFieldValue("studyContactId", val.id);
                    setName(formatMainContactName(val));
                    setFieldValue("email", val.email || "");
                    setFieldValue("phone", val.phone || "");
                    setFieldValue("nameVisibility", val.nameVisibility);
                    setFieldValue(
                      "emailVisibility",
                      val.email ? val.emailVisibility : false,
                    );
                    setFieldValue(
                      "phoneVisibility",
                      val.phone ? val.phoneVisibility : false,
                    );
                  }}
                  getOptionLabel={(option) => {
                    if (typeof option === "string") return option;
                    return formatMainContactName(option);
                  }}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    return (
                      <MuiTypography
                        key={key}
                        {...optionProps}
                        sx={{ fontSize: "14px" }}
                      >
                        {formatMainContactName(option)}
                      </MuiTypography>
                    );
                  }}
                  styles={{ width: "342px", ...formTextFieldStyles }}
                />
                {values.name !== initOtherStudyContact.firstName && (
                  <MuiFormControlLabel
                    label={t(
                      "studyRecruitment.form.displayNameOnPublicStudyPage",
                    )}
                    control={
                      <Field
                        component={FormikCheckbox}
                        type="checkbox"
                        name="nameVisibility"
                        disabled={!values.name}
                        styles={formCheckboxStyles.checkbox}
                      />
                    }
                    sx={formCheckboxStyles.label}
                  />
                )}
              </MuiStack>

              <MuiStack gap={0.25} width="257px">
                <Field
                  component={FormikTextField}
                  name="email"
                  label="studyRecruitment.form.emailAddress"
                  inputProps={{ maxLength: 10000 }}
                  disabled={!values.name}
                  required
                  styles={formTextFieldStyles}
                />
                <MuiFormControlLabel
                  label={t(
                    "studyRecruitment.form.displayEmailOnPublicStudyPage",
                  )}
                  control={
                    <Field
                      component={FormikCheckbox}
                      type="checkbox"
                      name="emailVisibility"
                      disabled={!values.email}
                      styles={formCheckboxStyles.checkbox}
                    />
                  }
                  sx={formCheckboxStyles.label}
                />
              </MuiStack>

              <MuiStack gap={0.25} width="179px">
                <Field
                  component={FormikTextField}
                  name="phone"
                  label="studyRecruitment.form.phone"
                  inputProps={{ maxLength: 10 }}
                  disabled={!values.email}
                  styles={formTextFieldStyles}
                  onChange={(e, setValue) => {
                    const numericValue = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10);
                    setValue(numericValue);
                  }}
                />
                <MuiFormControlLabel
                  label={t(
                    "studyRecruitment.form.displayPhoneOnPublicStudyPage",
                  )}
                  control={
                    <Field
                      component={FormikCheckbox}
                      type="checkbox"
                      name="phoneVisibility"
                      disabled={!values.phone}
                      styles={formCheckboxStyles.checkbox}
                    />
                  }
                  sx={formCheckboxStyles.label}
                />
              </MuiStack>
            </MuiStack>
          </MuiStack>

          <MuiStack gap={1.5}>
            <MuiStack alignItems="center" direction="row" gap={0.5}>
              <MuiTypography sx={formLabelStyles}>
                {t("studyRecruitment.labels.studyPreQuestionnaire")}
              </MuiTypography>
              <MuiTooltip
                title={t("studyRecruitment.tooltips.info")}
                placement="bottom"
              >
                <MuiIconButton
                  onClick={(event) =>
                    handleInfoDialogOpen(event, PRE_SCREENING_QUESTIONNAIRE)
                  }
                  sx={formInfoIconStyles.button}
                >
                  <MuiInfoIcon sx={formInfoIconStyles.icon} />
                </MuiIconButton>
              </MuiTooltip>
            </MuiStack>
            <MuiStack direction="row" gap={1.5}>
              <Field
                component={FormikTextField}
                name="prescreeningQuestionnaireUrl"
                label="studyRecruitment.form.urlLink"
                inputProps={{ maxLength: 10000 }}
                styles={{ flex: 1, ...formTextFieldStyles }}
              />
              <Field
                component={FormikTextField}
                name="prescreeningQuestionnaireEstimatedTime"
                label="studyRecruitment.form.estTimeToComplete"
                disabled={!values.prescreeningQuestionnaireUrl}
                onChange={(e, setValue) => {
                  const numericValue = e.target.value.replace(/\D/g, "");
                  setValue(numericValue);
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <MuiInputAdornment position="end">
                        <MuiTypography fontSize={14}>
                          {t("studyRecruitment.form.min")}
                        </MuiTypography>
                      </MuiInputAdornment>
                    ),
                  },
                  inputLabel: {
                    shrink: true,
                  },
                }}
                styles={{ width: "125px", ...formTextFieldStyles }}
              />
            </MuiStack>
          </MuiStack>

          <MuiStack gap={1.5}>
            <MuiTypography sx={formLabelStyles}>
              {t(
                "studyRecruitment.labels.studyPageInStanfordClinicalTrialsDirectory",
              )}
            </MuiTypography>

            <MuiStack alignItems="center" direction="row" gap={0.5}>
              <Field
                component={FormikTextField}
                name="officialStudyTitle"
                label={
                  <Trans i18nKey="studyRecruitment.form.officialStudyTitle" />
                }
                fullWidth
                multiline
                minRows={4}
                slotProps={{
                  input: {
                    readOnly: true,
                  },
                }}
                styles={formTextFieldStyles}
              />
              <MuiTooltip
                title={t("studyRecruitment.tooltips.info")}
                placement="bottom"
              >
                <MuiIconButton
                  onClick={(event) =>
                    handleInfoDialogOpen(event, OFFICIAL_STUDY_TITLE)
                  }
                  sx={formInfoIconStyles.button}
                >
                  <MuiInfoIcon sx={formInfoIconStyles.icon} />
                </MuiIconButton>
              </MuiTooltip>
            </MuiStack>

            <MuiStack alignItems="center" direction="row" gap={0.5}>
              <Field
                component={FormikTextField}
                name="studyTitle"
                label="studyRecruitment.form.studyTitle"
                required
                fullWidth
                multiline
                inputProps={{ maxLength: 900 }}
                styles={formTextFieldStyles}
              />
              <MuiTooltip
                title={t("studyRecruitment.tooltips.info")}
                placement="bottom"
              >
                <MuiIconButton
                  onClick={(event) => handleInfoDialogOpen(event, STUDY_TITLE)}
                  sx={formInfoIconStyles.button}
                >
                  <MuiInfoIcon sx={formInfoIconStyles.icon} />
                </MuiIconButton>
              </MuiTooltip>
            </MuiStack>

            <MuiStack alignItems="center" direction="row" gap={0.5}>
              <Field
                component={FormikTextField}
                name="purpose"
                label="studyRecruitment.form.purpose"
                required
                fullWidth
                multiline
                inputProps={{ maxLength: 10000 }}
                styles={formTextFieldStyles}
              />
              <MuiTooltip
                title={t("studyRecruitment.tooltips.info")}
                placement="bottom"
              >
                <MuiIconButton
                  onClick={(event) => handleInfoDialogOpen(event, PURPOSE)}
                  sx={formInfoIconStyles.button}
                >
                  <MuiInfoIcon sx={formInfoIconStyles.icon} />
                </MuiIconButton>
              </MuiTooltip>
            </MuiStack>

            <MuiStack alignItems="center" direction="row" gap={0.5}>
              <Field
                component={FormikTextField}
                name="inclusionCriteria"
                label="studyRecruitment.form.inclusionCriteria"
                placeholder={t(
                  "studyRecruitment.placeholders.inclusionCritera",
                )}
                required
                fullWidth
                multiline
                minRows={4}
                inputProps={{ maxLength: 10000 }}
                styles={formTextFieldStyles}
              />
              <MuiTooltip
                title={t("studyRecruitment.tooltips.info")}
                placement="bottom"
              >
                <MuiIconButton
                  onClick={(event) =>
                    handleInfoDialogOpen(event, INCLUSION_CRITERIA)
                  }
                  sx={formInfoIconStyles.button}
                >
                  <MuiInfoIcon sx={formInfoIconStyles.icon} />
                </MuiIconButton>
              </MuiTooltip>
            </MuiStack>

            <MuiStack alignItems="center" direction="row" gap={0.5}>
              <Field
                component={FormikTextField}
                name="exclusionCriteria"
                label="studyRecruitment.form.exclusionCriteria"
                placeholder={t(
                  "studyRecruitment.placeholders.exclusionCriteria",
                )}
                required
                fullWidth
                multiline
                minRows={4}
                inputProps={{ maxLength: 10000 }}
                styles={formTextFieldStyles}
              />
              <MuiTooltip
                title={t("studyRecruitment.tooltips.info")}
                placement="bottom"
              >
                <MuiIconButton
                  onClick={(event) =>
                    handleInfoDialogOpen(event, EXCLUSION_CRITERIA)
                  }
                  sx={formInfoIconStyles.button}
                >
                  <MuiInfoIcon sx={formInfoIconStyles.icon} />
                </MuiIconButton>
              </MuiTooltip>
            </MuiStack>
          </MuiStack>
        </MuiStack>

        <MuiStack alignSelf="flex-end" direction="row" gap={2}>
          <MuiButton
            type="button"
            variant="outlined"
            disabled={!dirty}
            onClick={() => setShowDiscardConfirmationDialog(true)}
            sx={buttonStyles}
          >
            <MuiTypography sx={{ fontSize: "16px" }}>
              {t("buttons.caps.cancel")}
            </MuiTypography>
          </MuiButton>
          <MuiButton
            type="button"
            variant="contained"
            disabled={!dirty || isSubmitting || !isValid}
            onClick={() => setShowSubmitConfirmationDialog(true)}
            sx={buttonStyles}
          >
            {isLoading[LOADER_TYPES.SAVE_STUDY_RECRUITMENT_DATA] ? (
              <MuiCircularProgress
                color="inherit"
                size="20px"
                sx={{ mx: "1em" }}
              />
            ) : (
              <MuiTypography sx={{ fontSize: "16px" }}>
                {t("buttons.caps.submit")}
              </MuiTypography>
            )}
          </MuiButton>
        </MuiStack>
      </MuiStack>

      <ConfirmationDialog
        open={showDiscardConfirmationDialog}
        onClose={handleDiscardConfirmationDialogClose}
        title={t("studyRecruitment.confirmationDialog.cancel.title")}
        message={t("studyRecruitment.confirmationDialog.cancel.content")}
        okLabel={t("buttons.caps.discardChanges")}
        cancelLabel={t("buttons.caps.keepEditing")}
      />

      <ConfirmationDialog
        open={showSubmitConfirmationDialog}
        onClose={handleSubmitConfirmationDialogClose}
        message={t(
          "studyRecruitment.confirmationDialog.submitStudyRecruitment",
        )}
        cancelLabel={t("buttons.caps.cancel")}
      />

      <InfoDialog
        open={openInfoDialog}
        handleClose={handleInfoDialogClose}
        title={infoDialogTitle()}
      >
        <MuiTypography
          sx={{ fontSize: "14px", lineHeight: "18px", whiteSpace: "pre-wrap" }}
        >
          {infoDialogContent()}
        </MuiTypography>
      </InfoDialog>
    </MuiStack>
  );
};

export default StudyRecruitmentForm;
